import type { Item } from 'types';

// Tie Types
export const TIE_TYPE_PRE_TIED = 'Pre Tied' as const;
export const TIE_TYPE_SELF_TIED = 'Self Tied' as const;

export const TieTypes = [TIE_TYPE_PRE_TIED, TIE_TYPE_SELF_TIED];

// David's Bridal color matches
// A product is considered a David's Bridal match when its title contains any of these color terms.
export const DAVIDS_BRIDAL_COLORS = [
  'Wine',
  'Eucalyptus',
  'Cinnamon',
  'Black',
  'Evergreen',
  'Dusty Sage',
  'Juniper',
  'Dusty Blue',
  'Marine',
  'Ballet',
  'Steel Blue',
  'Iris',
  'Olive',
  'Plum',
  'Apple',
  'Desert Coral',
  'Sand',
  'Chianti',
  'Gem',
  'Sky Blue',
  'Golden',
  'Quartz',
  'Champagne',
  'Sienna',
  'Chocolate',
  'Royal Blue',
  'Pastel Pink',
  'Latte',
  'Sunset Coral',
  'Berry',
  'White',
  'Platinum',
  'Butterscotch',
  'Amethyst',
] as const;

export const DAVIDS_BRIDAL_FILTER_LABEL = "David's Bridal Matches";

// Only these collection pages expose the David's Bridal Colors filter (matches long tie, bow tie, pocket squares).
export const DAVIDS_BRIDAL_FILTER_SLUGS = ['ties', 'accessories'];

// Defensive only: every two-tone product in the catalog today is also a "Pin Dot", so DAVIDS_BRIDAL_EXCLUDED_TERMS
// already rejects all of them before this is consulted. Kept for a future two-tone product with no pattern in its name.
const TWO_TONE_WHITE = /(?:\band|&)\s+white\b/;

// Catalog colors that merely contain a David's Bridal color as a substring but are a different color
// ("Aqua Marine" vs "Marine", "Persian Plum" vs "Plum"). The catalog carries both the plain and the
// prefixed color, so these are removed from the title before matching to stop them matching on the
// shorter name.
const COLLIDING_COLORS = /aqua\s+marine|persian\s+plum/g;

// David's Bridal matches are solid-color products only, so any patterned or textured variant is excluded
// even when its color name matches — e.g. "Wine Zig Zag Pocket Square" is not a match but "Wine Pocket
// Square" is. "Solid" is deliberately absent: it is the naming for exactly the products we want to keep.
export const DAVIDS_BRIDAL_EXCLUDED_TERMS = [
  'Zig Zag',
  'Plaid',
  'Striped',
  'Pin Dot',
  'Satin',
  'Paisley',
  'Micro Square',
  'Textured',
  'Floral',
];

// Spaces within a term are optional so both spellings are caught — the catalog writes "Microsquare" in
// titles but "Micro Square" as the pattern value, and the same drift could hit "Zig Zag" or "Pin Dot".
const EXCLUDED_TERMS = new RegExp(DAVIDS_BRIDAL_EXCLUDED_TERMS.map((term) => term.split(/\s+/).join('\\s*')).join('|'), 'i');

// Within the collections that expose the filter, only ties and pocket squares can be matches — the other
// accessories (cufflinks, tie bars, lapel pins, belts, suspenders) are excluded regardless of color.
export const DAVIDS_BRIDAL_CATEGORIES = ['Tie', 'Pocket Square'];

export const isDavidsBridalCategory = (category?: string | null): boolean => !!category && DAVIDS_BRIDAL_CATEGORIES.includes(category);

export const isDavidsBridalMatch = (title?: string | null): boolean => {
  if (!title) return false;

  if (EXCLUDED_TERMS.test(title)) return false;

  const lowerCaseTitle = title.toLowerCase().replace(COLLIDING_COLORS, '');

  // Two-tone products named "<Color> and White" (e.g. "Begonia and White Pin Dot Pre-Tied Bow Tie") are
  // only matches when the leading color is itself a David's Bridal color — the trailing "white" describes
  // the accent, not the product's color, so it shouldn't pull the product into the results on its own.
  // Every such title currently spells it "and"; "&" is accepted defensively since the marketing copy for
  // these same products uses it.
  const colors = TWO_TONE_WHITE.test(lowerCaseTitle) ? DAVIDS_BRIDAL_COLORS.filter((color) => color !== 'White') : DAVIDS_BRIDAL_COLORS;

  return colors.some((color) => lowerCaseTitle.includes(color.toLowerCase()));
};

// Both halves of the rule, so the collection filter has a single predicate to call.
export const isDavidsBridalProduct = (product: Pick<Item, 'category' | 'display_name'>): boolean =>
  isDavidsBridalCategory(product.category) && isDavidsBridalMatch(product.display_name);
